import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { normalizeUserAccess } from "../utils/featureAccess";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [didLogout, setDidLogout] = useState(false);

  const normalizeAndSetUser = useCallback((rawUser) => {
    if (!rawUser) {
      setUser(null);
      return null;
    }

    const normalizedUser = {
      ...normalizeUserAccess(rawUser),
      isLoggedIn: true,
    };
    setUser(normalizedUser);
    return normalizedUser;
  }, []);

  const refreshUser = useCallback(async (activeToken = token) => {
    if (!activeToken) {
      setUser(null);
      return null;
    }

    const res = await axios.get(`${API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${activeToken}` },
      timeout: 5000, // 5s — prevents infinite hang if backend is unreachable
    });

    return normalizeAndSetUser(res.data);
  }, [normalizeAndSetUser, token]);

  useEffect(() => {
    let isCancelled = false;

    if (token) {
      setLoading(true);

      // Safety net: if the request stalls beyond 6s, unblock the UI anyway.
      const safetyTimer = setTimeout(() => {
        if (!isCancelled) {
          setLoading(false);
        }
      }, 6000);

      refreshUser(token)
        .catch(() => {
          if (isCancelled) return;
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        })
        .finally(() => {
          clearTimeout(safetyTimer);
          if (!isCancelled) {
            setLoading(false);
          }
        });
    } else {
      // No token — resolve loading immediately so the app can render
      setLoading(false);
    }

    return () => {
      isCancelled = true;
    };
  }, [refreshUser, token, didLogout]);

  useEffect(() => {
    if (!token) return undefined;

    // Minimum gap between focus-triggered syncs (ms).
    // Prevents rapid-fire /api/auth/me calls when alt-tabbing during dev.
    const MIN_FOCUS_SYNC_INTERVAL = 10_000;
    let lastFocusSync = 0;

    const syncUser = () => {
      refreshUser(token).catch((error) => {
        if (error?.response?.status === 401) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
        // Background sync — transient failures (429, 5xx) are silently ignored.
      });
    };

    const onFocus = () => {
      const now = Date.now();
      if (now - lastFocusSync >= MIN_FOCUS_SYNC_INTERVAL) {
        lastFocusSync = now;
        syncUser();
      }
    };

    window.addEventListener("focus", onFocus);
    const intervalId = window.setInterval(syncUser, 30000);

    return () => {
      window.removeEventListener("focus", onFocus);
      window.clearInterval(intervalId);
    };
  }, [refreshUser, token]);


  const login = async (email, password) => {
    const res = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { access_token } = res.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setDidLogout(false);

    setLoading(true);
    try {
      return await refreshUser(access_token);
    } catch (error) {
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, nickname = null) => {
    const res = await axios.post(`${API_URL}/api/auth/register`, { email, password, nickname });
    const { access_token } = res.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);

    setLoading(true);
    try {
      return await refreshUser(access_token);
    } catch (error) {
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loginWithOAuth = async (provider, code, redirectUri = null) => {
    const res = await axios.post(`${API_URL}/api/auth/oauth/${provider}`, {
      code,
      redirect_uri: redirectUri,
    });
    const { access_token } = res.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setDidLogout(false);

    setLoading(true);
    try {
      return await refreshUser(access_token);
    } catch (error) {
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setDidLogout(true);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        isLoggedIn: Boolean(user?.isLoggedIn),
        login,
        register,
        loginWithOAuth,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
